import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useChatContext } from "stream-chat-react";

const noop = (..._args) => null;

const Context = createContext({
  handleInputChange: noop,
  handleMemberSelect: noop,
  handleSubmit: noop,
  members: [],
  name: "",
  errors: { name: null, members: null },
  createChannelType: undefined,
});

const getChannelTypeFromWorkspaceName = (workspace) => workspace.match(/.*__(team|messaging)/)?.[1];

const getUpsertAction = (workspace) => {
  if (workspace.match("Channel-Create")) return "create";
  if (workspace.match("Channel-Edit")) return "update";
};

export function AdminPanelForm({ children, defaultValues, workspace, onSubmit }) {
  const { client, channel, setActiveChannel } = useChatContext();
  const [channelName, setChannelName] = useState(defaultValues.name);
  const [channelMembers, setChannelMembers] = useState(defaultValues.members);
  const [formErrors, setFormErrors] = useState({ name: null, members: null });

  const formCreateChannelType = getChannelTypeFromWorkspaceName(workspace);
  const formAction = getUpsertAction(workspace);

  const createChannel = useCallback(
    async ({ name, members }) => {
      if (!formCreateChannelType || members.length === 0) return;
      const newChannel = await client.channel(formCreateChannelType, name, {
        name,
        members,
        demo: "team",
      });

      await newChannel.watch();

      setActiveChannel(newChannel);
    },
    [formCreateChannelType, setActiveChannel, client],
  );

  const updateChannel = useCallback(
    async ({ name, members }) => {
      if (name !== (channel?.data?.name || channel?.data?.id)) {
        await channel?.update({ name }, { text: `Channel name changed to ${name}` });
      }

      if (members?.length) {
        await channel?.addMembers(members);
      }
    },
    [channel],
  );

  // Helper function to validate channel name format
  const isValidChannelName = (name) => {
    // Check if name contains only alphanumeric characters, dashes, or underscores
    return /^[a-zA-Z0-9_-]+$/.test(name);
  };

  const validateForm = useCallback(
    ({ action, createChannelType, values }) => {
      let errors = { name: null, members: null };

      if (action === "create") {
        let nameError = null;
        if (!values.name && createChannelType === "team") {
          nameError = "Channel name is required";
        } else if (values.name && !isValidChannelName(values.name)) {
          nameError = "Channel name can only contain letters, numbers, dashes, and underscores";
        }
        errors = {
          name: nameError,
          members: values.members.length < 2 ? "At least one additional member is required" : null,
        };
      }

      if (
        action === "update" &&
        values.name === defaultValues.name &&
        values.members.length === 0
      ) {
        errors = {
          name: "Name not changed (change name or add members)",
          members: "No new members added (change name or add members)",
        };
      }

      if (action === "update" && values.name && !isValidChannelName(values.name)) {
        errors = {
          ...errors,
          name: "Channel name can only contain letters, numbers, dashes, and underscores",
        };
      }

      return Object.values(errors).some((v) => !!v) ? errors : null;
    },
    [defaultValues.name],
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const errors = validateForm({
        values: { name: channelName, members: channelMembers },
        action: formAction,
        createChannelType: formCreateChannelType,
      });

      if (errors) {
        setFormErrors(errors);
        return;
      }

      try {
        if (formAction === "create")
          await createChannel({ name: channelName, members: channelMembers });
        if (formAction === "update")
          await updateChannel({ name: channelName, members: channelMembers });
        onSubmit();
      } catch (err) {
        console.error(err);
      }
    },
    [
      formAction,
      formCreateChannelType,
      channelName,
      channelMembers,
      createChannel,
      updateChannel,
      onSubmit,
      validateForm,
    ],
  );

  const handleInputChange = useCallback((event) => {
    event.preventDefault();
    setChannelName(event.target.value);
  }, []);

  const handleMemberSelect = useCallback((event) => {
    setChannelMembers((prevMembers) => {
      const { value } = event.target;
      if (event.target.checked) {
        return prevMembers.length ? [...prevMembers, value] : [value];
      }
      return prevMembers?.filter((prevUser) => prevUser !== value);
    });
  }, []);

  useEffect(() => {
    setChannelName(defaultValues.name);
    setChannelMembers(defaultValues.members);
  }, [defaultValues, formCreateChannelType]);

  const value = useMemo(
    () => ({
      createChannelType: formCreateChannelType,
      errors: formErrors,
      name: channelName,
      members: channelMembers,
      handleInputChange,
      handleMemberSelect,
      handleSubmit,
    }),
    [
      formCreateChannelType,
      formErrors,
      channelName,
      channelMembers,
      handleInputChange,
      handleMemberSelect,
      handleSubmit,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export const useAdminPanelFormState = () => useContext(Context);
